// The per-variation rule behind the Dashboard's variation figures
// (useVariationsStats.ts): which variations count, and what each one adds to
// the total, approved and pending figures.
//
// WHICH VARIATIONS COUNT. claims_variations.status holds the Dashpivot form
// status: "valid", "invalid" or "pending" (lowercase, set by sync-variations
// and push-new-variation). Only "valid" (and a blank status, from before the
// column was filled) counts, the same rule as ProjectDetail.tsx, the
// useDashboardStats hook and the claims_project_dashboard view. The hook used
// to compare against "Approved", "Rejected" and "Void", which no stored value
// ever equals, so invalid and pending-status variations were counted in every
// total.
//
// Pure TypeScript, no React or supabase import, so variationStatsMath.test.ts
// can pin it with Deno.

export interface VariationRowInput {
  status?: string | null;
  debit_credit?: string | null;
  total?: number | string | null;
  discount_percent?: number | string | null;
  approved_amount?: number | string | null;
}

export interface VariationFigures {
  /** -1 for a credit, so it subtracts; 1 otherwise. */
  sign: 1 | -1;
  isCredit: boolean;
  /** The total after any discount, before the sign. */
  total: number;
  /** The approved amount, before the sign. Zero when nothing is approved. */
  approved: number;
  /** Total minus approved, before the sign. Less than one cent counts as nothing. */
  waiting: number;
}

/** Whether a variation with this status enters any Dashboard figure: valid, or blank. */
export function isCountedVariationStatus(status: string | null | undefined): boolean {
  return !status || status === "valid";
}

/** One counted variation's figures. Call only for a variation isCountedVariationStatus accepts. */
export function variationFigures(v: VariationRowInput): VariationFigures {
  const isCredit = v.debit_credit === "credit";
  const discount = Number(v.discount_percent) || 0;
  const total = Number(v.total || 0) * (1 - discount / 100);
  const approved = Number(v.approved_amount || 0);
  let waiting = total - approved;
  if (waiting < 0.01) waiting = 0;
  return { sign: isCredit ? -1 : 1, isCredit, total, approved, waiting };
}
